import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Button,
  Input,
  Label,
  Tag,
  Textarea,
  Select,
  Tabs,
  Card,
  Alert,
  Title,
  Text,
  Icon,
  Spinner,
} from '@nimbus-ds/components';
import {
  DisketteIcon,
  ExternalLinkIcon,
  FlagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@nimbus-ds/icons';
import { GA4ValidationPanel } from './GA4ValidationPanel';
import {
  CustomEventDefinition,
  CreateCustomEventRequest,
  TrackingConfig,
} from '@/api/customEvents';
import { storeService } from '@/api/store';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

const eventNameRegex = /^[a-z][a-z0-9_]*$/;

/** Element metadata captured from the Visual Editor for the confirmation card. */
interface CapturedElement {
  selector: string;
  matchCount: number;
  confidence: number | null;
  strategy: string | null;
  description: string | null;
  elementContext?: {
    tagName: string;
    textContent: string;
    attributes: Record<string, string>;
  };
}

/**
 * Confirmation card shown after capturing an element in the Visual Editor.
 * Surfaces what was picked and whether the selector resolves cleanly, so the
 * consultant isn't trusting a raw selector string blind.
 */
function CapturedElementCard({
  captured,
  currentSelector,
}: {
  captured: CapturedElement;
  currentSelector: string;
}) {
  const stale = captured.selector !== currentSelector;
  const count = captured.matchCount;

  const toneClasses =
    count === 1
      ? 'border-green-200 bg-green-50'
      : count === 0
      ? 'border-red-200 bg-red-50'
      : 'border-amber-200 bg-amber-50';

  const countMessage =
    count === 1
      ? 'Matches 1 element on the page'
      : count === 0
      ? "Matches 0 elements — this selector won't fire"
      : `Matches ${count} elements — the event fires for all of them`;

  const CountIcon = count === 1 ? CheckCircleIcon : ExclamationTriangleIcon;
  const countColor =
    count === 1 ? 'text-green-700' : count === 0 ? 'text-red-700' : 'text-amber-700';

  const elementLabel =
    captured.description ||
    (captured.elementContext
      ? `<${captured.elementContext.tagName}>${
          captured.elementContext.textContent
            ? ` "${captured.elementContext.textContent.slice(0, 60)}"`
            : ''
        }`
      : currentSelector);

  return (
    <div
      className={`rounded-md border p-3 space-y-2 ${
        stale ? 'border-muted bg-muted/30' : toneClasses
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon source={<FlagIcon />} />
        <Text fontWeight="medium" color="neutral-textHigh">
          Captured from Visual Editor
        </Text>
      </div>

      <Text color="neutral-textHigh">{elementLabel}</Text>

      {stale ? (
        <Text fontSize="caption">
          Selector edited — the match count no longer reflects the current selector. Re-open the
          Visual Editor to re-verify.
        </Text>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className={`flex items-center gap-1 text-sm ${countColor}`}>
            <Icon source={<CountIcon />} color="currentColor" />
            {countMessage}
          </span>
          {captured.confidence != null && (
            <Tag appearance="neutral">{captured.confidence}% confidence</Tag>
          )}
          {captured.strategy && <Tag appearance="neutral">{captured.strategy}</Tag>}
        </div>
      )}
    </div>
  );
}

const customEventSchema = z.object({
  event_name: z
    .string()
    .min(1, 'Event name is required')
    .max(50, 'Event name must be 50 characters or less')
    .regex(eventNameRegex, 'Event name must be snake_case (lowercase, underscores only)'),
  display_name: z.string().min(1, 'Display name is required').max(100),
  description: z.string().max(500).optional(),
  category: z.enum(['interaction', 'conversion_signal', 'engagement', 'navigation']),
});

type CustomEventFormData = z.infer<typeof customEventSchema>;

interface CustomEventFormProps {
  storeId: string;
  existingEvent?: CustomEventDefinition;
  onSave: (data: CreateCustomEventRequest | Partial<CustomEventDefinition>) => Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
}

const TAB_ORDER = ['basic', 'tracking', 'ga4'];

export function CustomEventForm({
  storeId,
  existingEvent,
  onSave,
  onCancel,
  isSaving = false,
}: CustomEventFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isEditMode = !!existingEvent;
  const [activeTab, setActiveTab] = useState('basic');
  const [ga4EventName, setGA4EventName] = useState(
    existingEvent?.ga4_config?.ga4_event_name || ''
  );

  // Tracking configuration state
  const [trackingMode, setTrackingMode] = useState<'manual' | 'auto'>(
    existingEvent?.tracking_config?.mode || 'manual'
  );
  const [interactionType, setInteractionType] = useState<
    'click' | 'visibility' | 'scroll' | 'media_view'
  >(
    existingEvent?.tracking_config?.auto_config?.interaction_type === 'form_change'
      ? 'click'
      : existingEvent?.tracking_config?.auto_config?.interaction_type || 'click'
  );
  const [selector, setSelector] = useState(
    existingEvent?.tracking_config?.auto_config?.click_config?.selector ||
    existingEvent?.tracking_config?.auto_config?.visibility_config?.selector ||
    existingEvent?.tracking_config?.auto_config?.media_view_config?.selector ||
    ''
  );
  // Element captured from the Visual Editor this session (confirmation card).
  const [capturedElement, setCapturedElement] = useState<CapturedElement | null>(null);

  // media_view ("Image Seen") config state.
  const [mediaMatchBy, setMediaMatchBy] = useState<'selector' | 'src_pattern'>(
    existingEvent?.tracking_config?.auto_config?.media_view_config?.match_by || 'src_pattern'
  );
  const [srcPattern, setSrcPattern] = useState(
    existingEvent?.tracking_config?.auto_config?.media_view_config?.src_pattern || ''
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<CustomEventFormData>({
    resolver: zodResolver(customEventSchema),
    defaultValues: {
      event_name: existingEvent?.event_name || '',
      display_name: existingEvent?.display_name || '',
      description: existingEvent?.description || '',
      category: existingEvent?.category || 'interaction',
    },
  });

  // Sync state when existingEvent changes (e.g., opening edit dialog with different event)
  useEffect(() => {
    if (existingEvent) {
      setGA4EventName(existingEvent.ga4_config?.ga4_event_name || '');
      setTrackingMode(existingEvent.tracking_config?.mode || 'manual');
      setInteractionType(
        existingEvent.tracking_config?.auto_config?.interaction_type === 'form_change'
          ? 'click'
          : existingEvent.tracking_config?.auto_config?.interaction_type || 'click'
      );
      setSelector(
        existingEvent.tracking_config?.auto_config?.click_config?.selector ||
        existingEvent.tracking_config?.auto_config?.visibility_config?.selector ||
        existingEvent.tracking_config?.auto_config?.media_view_config?.selector ||
        ''
      );
      setMediaMatchBy(
        existingEvent.tracking_config?.auto_config?.media_view_config?.match_by || 'src_pattern'
      );
      setSrcPattern(
        existingEvent.tracking_config?.auto_config?.media_view_config?.src_pattern || ''
      );
      setCapturedElement(null);
      // Reset form values
      reset({
        event_name: existingEvent.event_name || '',
        display_name: existingEvent.display_name || '',
        description: existingEvent.description || '',
        category: existingEvent.category || 'interaction',
      });
    } else {
      // Reset to defaults for new event
      setGA4EventName('');
      setTrackingMode('manual');
      setInteractionType('click');
      setSelector('');
      setMediaMatchBy('src_pattern');
      setSrcPattern('');
      setCapturedElement(null);
      reset({
        event_name: '',
        display_name: '',
        description: '',
        category: 'interaction',
      });
    }
  }, [existingEvent, reset]);

  // Listen for tracking data from Visual Editor
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'TNX_CUSTOM_EVENT_TRACKING_SELECTED') return;

      const { trackingData } = event.data;
      if (!trackingData) return;
      console.log('[CustomEventForm] Received tracking data from Visual Editor:', trackingData);

      // Update form state with received tracking data
      setTrackingMode('auto');
      setSelector(trackingData.selector || '');
      setInteractionType(trackingData.interactionType || 'click');

      // media_view captures arrive with a match mode and (for images) a
      // pre-derived filename glob.
      if (trackingData.interactionType === 'media_view') {
        setMediaMatchBy(trackingData.matchBy === 'selector' ? 'selector' : 'src_pattern');
        if (trackingData.srcPattern) setSrcPattern(trackingData.srcPattern);
      }

      // Store the captured-element metadata for the confirmation card.
      const matchCount =
        typeof trackingData.matchCount === 'number' ? trackingData.matchCount : 0;
      setCapturedElement({
        selector: trackingData.selector || '',
        matchCount,
        confidence:
          typeof trackingData.confidence === 'number' ? trackingData.confidence : null,
        strategy: trackingData.strategy || null,
        description: trackingData.description || null,
        elementContext: trackingData.elementContext,
      });

      // Prefill the display name from the Visual Editor suggestion, but only
      // if the consultant hasn't already typed one.
      if (trackingData.displayName && !getValues('display_name')) {
        setValue('display_name', trackingData.displayName, { shouldValidate: true });
      }

      // Switch to tracking tab to show the updated values
      setActiveTab('tracking');

      const isImagePattern =
        trackingData.interactionType === 'media_view' && !!trackingData.srcPattern;
      toast({
        title: isImagePattern ? 'Image captured' : 'Element captured',
        description: isImagePattern
          ? `Filename pattern: ${trackingData.srcPattern}`
          : typeof trackingData.matchCount === 'number'
          ? `Selector matches ${matchCount} element${matchCount === 1 ? '' : 's'} on the page.`
          : `Selector "${trackingData.selector}" received from the Visual Editor.`,
      });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toast, getValues, setValue]);

  const buildTrackingConfig = (): TrackingConfig => {
    if (trackingMode === 'manual') {
      return { mode: 'manual' };
    }

    // Auto mode - build config based on interaction type
    const autoConfig: TrackingConfig = {
      mode: 'auto',
      auto_config: {
        interaction_type: interactionType,
      },
    };

    if (interactionType === 'click' && selector) {
      autoConfig.auto_config!.click_config = { selector };
    } else if (interactionType === 'visibility' && selector) {
      // threshold 0 = fire as soon as any part of the element is visible.
      autoConfig.auto_config!.visibility_config = {
        selector,
        threshold: 0,
      };
    } else if (interactionType === 'media_view') {
      autoConfig.auto_config!.media_view_config = {
        match_by: mediaMatchBy,
        threshold: 0,
        ...(mediaMatchBy === 'selector'
          ? { selector }
          : { src_pattern: srcPattern.trim() }),
      };
    } else if (interactionType === 'scroll') {
      autoConfig.auto_config!.scroll_config = { depth_percentage: 50 };
    }

    return autoConfig;
  };

  const onSubmit = async (data: CustomEventFormData) => {
    const trackingConfig = buildTrackingConfig();

    const payload: CreateCustomEventRequest | Partial<CustomEventDefinition> = {
      ...(isEditMode
        ? {}
        : {
            store_id: storeId,
            event_name: data.event_name,
          }),
      display_name: data.display_name,
      description: data.description,
      category: data.category,
      tracking_config: trackingConfig,
      ga4_config: ga4EventName ? { ga4_event_name: ga4EventName } : null,
    };

    await onSave(payload);
  };

  const handleOpenVisualEditor = async () => {
    if (!user || !user.store_id) {
      toast({
        variant: 'destructive',
        title: 'Sesión inválida',
        description: 'No se pudo identificar tu tienda actual. Por favor, vuelve a iniciar sesión.',
      });
      return;
    }

    let storeUrl: string | undefined = user.store_url || user.original_domain;
    if (!storeUrl) {
      try {
        const storeInfo = await storeService.getStore(user.store_id);
        storeUrl = storeInfo?.domain;
      } catch (err) {
        console.error('Failed to fetch store info for visual editor:', err);
      }
    }

    if (!storeUrl) {
      toast({
        variant: 'destructive',
        title: 'Tienda sin dominio',
        description: `La tienda ${user.store_id} no tiene un dominio configurado. El editor visual no puede abrirse hasta que la tienda esté completamente conectada vía TiendaNube.`,
      });
      return;
    }

    // Normalize URL: remove any existing protocol and add https://
    // This handles cases where TiendaNube API returns domains with http:// prefix
    storeUrl = storeUrl.replace(/^https?:\/\//, '');
    storeUrl = `https://${storeUrl}`;

    // Add tnx_visual_editor parameter + events_only mode to activate editor in track-event mode only
    const separator = storeUrl.includes('?') ? '&' : '?';
    const editorUrl = `${storeUrl}${separator}tnx_visual_editor=true&tnx_events_only=true`;

    const editorWindow = window.open(editorUrl, 'VisualEditor', 'width=1400,height=900');

    if (editorWindow) {
      toast({
        title: 'Editor Visual Abierto',
        description: 'El editor visual se está cargando en una nueva ventana.',
      });
    } else {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo abrir el editor. Por favor, permite ventanas emergentes para este sitio.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Tabs
        fullWidth
        selected={Math.max(0, TAB_ORDER.indexOf(activeTab))}
        onTabSelect={(index) => setActiveTab(TAB_ORDER[index])}
      >
        {/* Tab 1: Basic Info */}
        <Tabs.Item label="Basic Info">
          <div className="space-y-4">
            <Card>
              <Card.Header>
                <Title as="h3">Basic Information</Title>
                <Text>Define the custom event name and category.</Text>
              </Card.Header>
              <Card.Body>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_name">
                      Event Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="event_name"
                      {...register('event_name')}
                      placeholder="e.g., size_guide_opened"
                      disabled={isEditMode}
                    />
                    {errors.event_name && (
                      <p className="text-sm text-red-600">{errors.event_name.message}</p>
                    )}
                    <Text fontSize="caption">
                      Must be snake_case (lowercase letters, numbers, underscores only). Cannot be
                      changed after creation.
                    </Text>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="display_name">
                      Display Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="display_name"
                      {...register('display_name')}
                      placeholder="e.g., Size Guide Opened"
                    />
                    {errors.display_name && (
                      <p className="text-sm text-red-600">{errors.display_name.message}</p>
                    )}
                    <Text fontSize="caption">
                      Human-readable name for the UI (can be changed later).
                    </Text>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">
                      Category <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      id="category"
                      name="category"
                      defaultValue={existingEvent?.category || 'interaction'}
                      onChange={(e) =>
                        setValue(
                          'category',
                          e.target.value as
                            | 'interaction'
                            | 'conversion_signal'
                            | 'engagement'
                            | 'navigation'
                        )
                      }
                    >
                      <Select.Option value="interaction" label="Interaction" />
                      <Select.Option value="conversion_signal" label="Conversion Signal" />
                      <Select.Option value="engagement" label="Engagement" />
                      <Select.Option value="navigation" label="Navigation" />
                    </Select>
                    {errors.category && (
                      <p className="text-sm text-red-600">{errors.category.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      {...register('description')}
                      placeholder="Optional description of what this event tracks..."
                      lines={3}
                    />
                    {errors.description && (
                      <p className="text-sm text-red-600">{errors.description.message}</p>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Tabs.Item>

        {/* Tab 2: Tracking Configuration */}
        <Tabs.Item label="Tracking">
          <div className="space-y-4">
            <Card>
              <Card.Header>
                <Title as="h3">Tracking Configuration</Title>
                <Text>Configure how this event is tracked.</Text>
              </Card.Header>
              <Card.Body>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tracking-mode">Tracking Mode</Label>
                    <Select
                      id="tracking-mode"
                      name="tracking-mode"
                      value={trackingMode}
                      onChange={(e) => setTrackingMode(e.target.value as 'manual' | 'auto')}
                    >
                      <Select.Option value="manual" label="Manual (via JavaScript API)" />
                      <Select.Option value="auto" label="Automatic (element-based)" />
                    </Select>
                    <Text fontSize="caption">
                      {trackingMode === 'manual'
                        ? 'Track this event by calling tnx.track("event_name", properties) in your code.'
                        : 'Automatically track when users interact with specific elements.'}
                    </Text>
                  </div>

                  {trackingMode === 'auto' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="interaction-type">Interaction Type</Label>
                        <Select
                          id="interaction-type"
                          name="interaction-type"
                          value={interactionType}
                          onChange={(e) =>
                            setInteractionType(
                              e.target.value as 'click' | 'visibility' | 'scroll' | 'media_view'
                            )
                          }
                        >
                          <Select.Option value="click" label="Click" />
                          <Select.Option
                            value="visibility"
                            label="Visibility (element appears on screen)"
                          />
                          <Select.Option value="media_view" label="Image Seen" />
                          <Select.Option value="scroll" label="Scroll Depth" />
                        </Select>
                      </div>

                      {interactionType === 'media_view' && (
                        <div className="space-y-2">
                          <Label htmlFor="media-match-by">Match image by</Label>
                          <Select
                            id="media-match-by"
                            name="media-match-by"
                            value={mediaMatchBy}
                            onChange={(e) =>
                              setMediaMatchBy(e.target.value as 'selector' | 'src_pattern')
                            }
                          >
                            <Select.Option value="src_pattern" label="Filename pattern" />
                            <Select.Option value="selector" label="CSS selector" />
                          </Select>
                          <Text fontSize="caption">
                            Filename pattern matches the image URL — robust against theme and
                            layout changes. CSS selector targets a specific element.
                          </Text>
                        </div>
                      )}

                      {interactionType !== 'scroll' &&
                        (interactionType !== 'media_view' || mediaMatchBy === 'selector') && (
                        <div className="space-y-2">
                          <Label htmlFor="css-selector">
                            CSS Selector <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="css-selector"
                            value={selector}
                            onChange={(e) => setSelector(e.target.value)}
                            placeholder={
                              interactionType === 'click'
                                ? 'e.g., .add-to-cart-button, #size-guide-link'
                                : interactionType === 'visibility'
                                ? 'e.g., .product-reviews, #testimonials-section'
                                : 'e.g., .product-gallery img, #hero-banner img'
                            }
                          />
                          <Text fontSize="caption">
                            {interactionType === 'click' && 'CSS selector for the element(s) to track clicks on.'}
                            {interactionType === 'visibility' && 'CSS selector for the element(s) to track when they become visible.'}
                            {interactionType === 'media_view' && 'CSS selector for the image element(s) to track when they become visible.'}
                          </Text>
                        </div>
                      )}

                      {interactionType === 'scroll' && (
                        <Text fontSize="caption">
                          Fires once per pageview when the visitor scrolls to 50% of the page.
                        </Text>
                      )}

                      {interactionType === 'media_view' && mediaMatchBy === 'src_pattern' && (
                        <div className="space-y-2">
                          <Label htmlFor="src-pattern">
                            Filename pattern <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="src-pattern"
                            value={srcPattern}
                            onChange={(e) => setSrcPattern(e.target.value)}
                            placeholder="e.g., *hero-banner* or *dsc04033*"
                            maxLength={200}
                          />
                          <Text fontSize="caption">
                            Matched against the image URL. Use <code>*</code> as a wildcard —{' '}
                            <code>*dsc04033*</code> matches any size variant the CDN generates
                            (e.g. <code>dsc04033-…-1024-1024.jpg</code>). Max 200 characters.
                          </Text>
                        </div>
                      )}

                      {capturedElement &&
                        interactionType !== 'scroll' &&
                        (interactionType !== 'media_view' || mediaMatchBy === 'selector') && (
                          <CapturedElementCard
                            captured={capturedElement}
                            currentSelector={selector}
                          />
                        )}
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>

            <Alert appearance="primary" title="Visual Editor">
              <div className="space-y-3">
                <Text>
                  For easier element selection, use the <strong>Visual Editor</strong> to
                  point-and-click on elements:
                </Text>
                <Button
                  type="button"
                  onClick={handleOpenVisualEditor}
                  appearance="neutral"
                  fullWidth
                >
                  <Icon source={<ExternalLinkIcon />} color="currentColor" />
                  Open Visual Editor
                </Button>
              </div>
            </Alert>
          </div>
        </Tabs.Item>

        {/* Tab 3: GA4 Integration */}
        <Tabs.Item label="GA4 Integration">
          <div className="space-y-4">
            <GA4ValidationPanel
              eventId={existingEvent?.event_id}
              ga4EventName={ga4EventName}
              onGA4EventNameChange={setGA4EventName}
              disabled={isSaving}
            />
          </div>
        </Tabs.Item>
      </Tabs>

      {/* Form Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" appearance="neutral" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" appearance="primary" disabled={isSaving}>
          {isSaving ? (
            <>
              <Spinner size="small" />
              Saving...
            </>
          ) : (
            <>
              <Icon source={<DisketteIcon />} color="currentColor" />
              {isEditMode ? 'Update Event' : 'Create Event'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
